import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, ListPlus } from "lucide-react";
import RFIQuestionItem from "./RFIQuestionItem";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RFIQuestionList({ rfiId, questions, onQuestionsUpdate, isEditing = true }) {
  const [bulkCount, setBulkCount] = useState(1);
  const [isAddingBulk, setIsAddingBulk] = useState(false);

  const handleAddSingleQuestion = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/rfis/${rfiId}/questions`,
        { question_text: "", status: "Open", response: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onQuestionsUpdate(response.data.questions);
      toast.success("Question added");
    } catch (error) {
      console.error("Failed to add question:", error);
      toast.error("Failed to add question");
    }
  };

  const handleAddBulkQuestions = async () => {
    if (bulkCount < 1 || bulkCount > 50) {
      toast.error("Please enter a number between 1 and 50");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/rfis/${rfiId}/questions/bulk`,
        { count: parseInt(bulkCount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onQuestionsUpdate(response.data.questions);
      toast.success(`${bulkCount} questions added`);
      setBulkCount(1);
      setIsAddingBulk(false);
    } catch (error) {
      console.error("Failed to add bulk questions:", error);
      toast.error("Failed to add bulk questions");
    }
  };

  const handleUpdateQuestion = async (questionId, updates) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API}/rfis/${rfiId}/questions/${questionId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onQuestionsUpdate(response.data.questions);
      toast.success("Question updated");
    } catch (error) {
      console.error("Failed to update question:", error);
      toast.error("Failed to update question");
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${API}/rfis/${rfiId}/questions/${questionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onQuestionsUpdate(response.data.questions);
      toast.success("Question deleted");
    } catch (error) {
      console.error("Failed to delete question:", error);
      toast.error("Failed to delete question");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">
          Questions ({questions?.length || 0})
        </Label>
        {isEditing && (
          <div className="flex gap-2">
            {isAddingBulk ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={bulkCount}
                  onChange={(e) => setBulkCount(e.target.value)}
                  className="w-20"
                  placeholder="#"
                />
                <Button size="sm" onClick={handleAddBulkQuestions}>
                  Add {bulkCount}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsAddingBulk(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={handleAddSingleQuestion}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Question
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsAddingBulk(true)}>
                  <ListPlus className="h-4 w-4 mr-1" />
                  Bulk Add
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {!questions || questions.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
          {isEditing ? "No questions yet. Click 'Add Question' to get started." : "No questions in this RFI."}
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {questions.map((question) => (
            <RFIQuestionItem
              key={question.id}
              question={question}
              onUpdate={handleUpdateQuestion}
              onDelete={handleDeleteQuestion}
              isEditing={isEditing}
            />
          ))}
        </div>
      )}
    </div>
  );
}
